import { HttpClientModule } from '@angular/common/http';
import localeFrExtra from '@angular/common/locales/extra/fr';
import localeFr from '@angular/common/locales/fr';
import {
    CUSTOM_ELEMENTS_SCHEMA,
    LOCALE_ID,
    NgModule
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { NavMenuComponent } from './components/nav-menu/nav-menu.component';
import { LoadingTask } from './tools/loading-task';
import { ConfirmBoxDialog } from './tools/ui/confirm-box/confirm-box.component';
import { CustomSnackBarComponent } from './tools/ui/custom-snackbar/custom-snackbar.component';
import { DescriptionStationComponent } from './components/description-station/description-station.component';
import { IBDComponent } from './components/ibd/ibd.component';
import { IBGNComponent } from './components/ibgn/ibgn.component';
import { MPCEComponent } from './components/mpce/mpce.component';
import { PreventDoubleClickDirective } from './tools/directive/prevent-double-click.directive';
import { InterventionListComponent } from './components/intervention-list/intervention-list.component';
import { IBDPrelevementDialog } from './tools/ui/ibd-prelevement-box/ibd-prelevement-box';
import { SpeedDialFabComponent } from './components/speed-dial-fab/speed-dial-fab.component';
import { InterventionDashboardComponent } from './components/intervention-dashboard/intervention-dashboard.component';
import { PlanPrelevementComponent } from './components/plan-prelevement/plan-prelevement.component';
import { NgCircleProgressModule } from 'ng-circle-progress';

import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { SubstratAndVitesseDialogComponent } from './tools/ui/substrat-and-vitesse-dialog/substrat-and-vitesse-dialog.component';
import { PrelevementDialogComponent } from './tools/ui/prelevement-dialog/prelevement-dialog.component';
import { AnalysisComponent } from './components/analysis/analysis.component';

import { TaxonDialogComponent } from './tools/ui/taxon-dialog/taxon-dialog.component';
import { AddPhotoComponent } from './components/add-photo/add-photo.component';
import { WebcamModule } from 'ngx-webcam';
import { DocumentsComponent } from './components/documents/documents.component';
import { MultipleFileUploadBoxDialog } from './tools/ui/mutilple-file-upload-box/multiple-file-upload-box';
import { CoreData } from './tools/core-data';
import { AnalysisListComponent } from './components/analysis-list/analysis-list.component';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { TextFieldModule } from '@angular/cdk/text-field';
import { EauComponent } from './components/eau/eau.component';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatBadgeModule } from '@angular/material/badge';
import { NotationComponent } from './components/notation/notation.component';
import { ServiceWorkerModule, SwRegistrationOptions } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { PictureViewerComponent } from './components/picture-viewer/picture-viewer.component';
import { ClickWaitDirective } from './tools/directive/button-with-promise';
import { EditTextBoxDialog } from './tools/ui/edit-text-box/edit-text-box';
import { ExportComponent } from './components/export/export.component';
import { FocusDirective } from './tools/directive/focus.directive';
import { CanNavigateGuard } from './tools/guards/navigation-guard';
import { WaterDialogBoxDialog } from './tools/ui/water-measure-box/water-measure-box';
import { IbdMeasureBoxComponent } from './tools/ui/ibd-measure-box/ibd-measure-box.component';
import { IbgnMeasureBoxComponent } from './tools/ui/ibgn-measure-box/ibgn-measure-box.component';
import { MPCEMGCEMeasureBoxComponent } from './tools/ui/mpce-mgce-measure-box/mpce-mgce-measure-box.component';
import { TakePhotoComponent } from './components/take-photo/take-photo.component';
import { ErrorComponent } from './error.component';
import { LacComponent } from './components/lac/lac.component';
import { LacMeasureBoxComponent } from './tools/ui/lac-measure-dialog/lac-measure-box.component';
import { LacPhytoEchantComponent } from './tools/ui/lac-phyto-echant-dialog/lac-phyto-echant-dialog.component';
import { LacPonctuelDialogomponent } from './tools/ui/lac-ponctuel-dialog/lac-pontuel-dialog.component';
import { MetrologieComponent } from './components/metrologie/metrologie.component';
import { SondeBoxComponent } from './tools/ui/sonde-box/sonde-box.component';
import { RoleGuard } from './tools/guards/role.guard';
import { RedirectComponent } from './components/redirect/redirect.component';
import { AdminImportComponent } from './components/admin-import/admin-import.component';
import { IBMRComponent } from './components/ibmr/ibmr.component';
import { MGCEComponent } from './components/mgce/mgce.component';
import { MGCESubstratBoxComponent } from './tools/ui/mgce-substrat-box/mgce-substrat-box.component';
import { TransectMeasureBoxComponent } from './tools/ui/transect-mesure-box/transect-measure-box.component';
import { IMLComponent } from './components/iml/iml.component';
import { IMLSubstratBoxComponent } from './tools/ui/iml-substrat-box/iml-substrat-box.component';
import { SecchiBoxComponent } from './tools/ui/secchi-box/secchi-box.component';
import { MatTableModule } from '@angular/material/table';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogModule } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { DatePipe, registerLocaleData } from '@angular/common';
import { SubMenuComponent } from './components/sub-menu/sub-menu.component';
import { ChangeNavigationService } from './tools/change-navigation-service';
import { TaxonIBMRBoxComponent } from './tools/ui/taxon-ibmr-box/taxon-ibmr-box.component';
import { AnalyseIbmrComponent } from './components/analyse-ibmr/analyse-ibmr.component';
import { SplitTaxonBoxComponent } from './tools/ui/split-taxon-box/split-taxon-box.component';
import { SampleCodeBoxComponent } from './tools/ui/cyano-creation-box/sample-code-box.component';
import { AnalyseCyanoComponent } from './components/analyse-cyano/analyse-cyano.component';
import { TaxonCyanoBoxComponent } from './tools/ui/taxon-cyano-box/taxon-cyano-box.component';
import { OxygeneCalibrationBoxComponent } from './tools/ui/oxygene-calibration-box/oxygene-calibration-box.component';
import { HistoryBoxComponent } from './tools/ui/history-box/history-box.component';
import { ConductiviteCalibrationBoxComponent } from './tools/ui/conductivite-calibration-box/conductivite-calibration-box.component';
import { PHCalibrationBoxComponent } from './tools/ui/pH-calibration-box/pH-calibration-box.component';
import { LogistiqueComponent } from './components/logistique/logistique.component';
import { InterventionCreationBoxComponent } from './tools/ui/intervention-creation-box/intervention-creation-box.component';
import { SamplingPointMapComponent } from './components/sampling-point-map/sampling-point-map.component';

@NgModule({
    declarations: [
        AppComponent,
        CustomSnackBarComponent,
        SpeedDialFabComponent,
        ConfirmBoxDialog,
        IBDPrelevementDialog,
        SecchiBoxComponent,
        MultipleFileUploadBoxDialog,
        TransectMeasureBoxComponent,
        MGCESubstratBoxComponent,
        LacMeasureBoxComponent,
        LacPhytoEchantComponent,
        LacPonctuelDialogomponent,
        SubstratAndVitesseDialogComponent,
        PreventDoubleClickDirective,
        ClickWaitDirective,
        FocusDirective,
        NavMenuComponent,
        DescriptionStationComponent,
        IBDComponent,
        IBGNComponent,
        MPCEComponent,
        InterventionListComponent,
        InterventionDashboardComponent,
        PlanPrelevementComponent,
        PrelevementDialogComponent,
        AnalysisComponent,
        TaxonDialogComponent,
        AddPhotoComponent,
        DocumentsComponent,
        AnalysisListComponent,
        RedirectComponent,
        EauComponent,
        NotationComponent,
        PictureViewerComponent,
        EditTextBoxDialog,
        WaterDialogBoxDialog,
        IbdMeasureBoxComponent,
        ExportComponent,
        IbdMeasureBoxComponent,
        IbgnMeasureBoxComponent,
        MPCEMGCEMeasureBoxComponent,
        IMLSubstratBoxComponent,
        TakePhotoComponent,
        ErrorComponent,
        LacComponent,
        MetrologieComponent,
        SondeBoxComponent,
        AdminImportComponent,
        IBMRComponent,
        MGCEComponent,
        IMLComponent,
        SubMenuComponent,
        TaxonIBMRBoxComponent,
        AnalyseIbmrComponent,
        SplitTaxonBoxComponent,
        SampleCodeBoxComponent,
        AnalyseCyanoComponent,
        TaxonCyanoBoxComponent,
        OxygeneCalibrationBoxComponent,
        ConductiviteCalibrationBoxComponent,
        HistoryBoxComponent,
        PHCalibrationBoxComponent,
        LogistiqueComponent,
        InterventionCreationBoxComponent,
        SamplingPointMapComponent
    ],
    imports: [
        HttpClientModule,
        FormsModule,
        BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        ReactiveFormsModule,
        TextFieldModule,
        MatFormFieldModule,
        MatTableModule,
        MatSnackBarModule,
        MatInputModule,
        MatButtonModule,
        MatProgressBarModule,
        MatTooltipModule,
        MatDialogModule,
        MatIconModule,
        MatNativeDateModule,
        MatDatepickerModule,
        MatSelectModule,
        MatSlideToggleModule,
        MatButtonToggleModule,
        MatAutocompleteModule,
        MatChipsModule,
        MatDividerModule,
        MatExpansionModule,
        MatCheckboxModule,
        MatBadgeModule,
        NgCircleProgressModule.forRoot(),
        NgxMaterialTimepickerModule,
        WebcamModule,
        ServiceWorkerModule.register('ngsw-worker.js'),
    ],
    providers: [
        { provide: LOCALE_ID, useValue: 'fr-FR' },
        { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { disableClose: true, hasBackdrop: true } },
        {
            provide: SwRegistrationOptions,
            useFactory: () => ({ enabled: environment.production }),
        },
        CanNavigateGuard,
        RoleGuard,
        LoadingTask,
        CoreData,
        DatePipe,
        ChangeNavigationService
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }

registerLocaleData(localeFr, 'fr-FR', localeFrExtra);